import { useState } from "react";

import { ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/outline";

import { IPart } from "../../models/Part";
import { FormatType } from "../../models/EbayOffer";
import { ISerpApiSearchResult } from "../../models/PartLookup";
import PriceFormat from "../../utils/priceFormat";
import { summarisePrices } from "../../utils/intelligencePricing";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import useNotification from "../notifications/useNotifications";
import UnlistedIntelligenceModal from "../ebayListings/unlistedTable/UnlistedIntelligenceModal";
import UnlistedKTypeUpdate from "../ebayListings/unlistedTable/UnlistedKTypeUpdate";

interface IOptimisationLiveListingCellProps {
  part: IPart;
  onStageAvgPrice: (partId: string, price: number) => void;
  stagingPrice: boolean;
}

// Live listing summary for the optimisation table: current title/price, intelligence
// min/avg/max from active eBay listings with stage-avg-price, a refresh, and KTYPE warning/fix.
// Nothing here revises the live eBay listing.
export default function OptimisationLiveListingCell({
  part,
  onStageAvgPrice,
  stagingPrice,
}: IOptimisationLiveListingCellProps) {
  const { addNotification } = useNotification();
  const { intelligencePricingForPart } = usePartsLookupMutations(part.id);

  const offer = part.offers?.at(0);
  const price =
    offer?.format === FormatType.Auction ? offer?.auctionStartPrice : offer?.buyItNowPrice;

  const missingKType = !part.vehicle?.kType;

  const [results, setResults] = useState<ISerpApiSearchResult[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);

  const summary = results
    ? summarisePrices(results)
    : part.intelligenceAverage != null
      ? {
          min: part.intelligenceMin ?? part.intelligenceAverage,
          max: part.intelligenceMax ?? part.intelligenceAverage,
          avg: part.intelligenceAverage,
        }
      : null;

  const activeCount = results ? results.length : part.intelligenceActiveCount;

  const runLookup = async (openModal: boolean) => {
    setLookupLoading(true);
    try {
      const response = await intelligencePricingForPart.mutateAsync(part.id);
      const lookupResults = response?.data ?? [];
      setResults(lookupResults);

      if (openModal) setModalOpen(true);

      if (lookupResults.length === 0) {
        addNotification({ primaryText: "No intelligence results found", variant: "warn" });
      }
    } catch {
      addNotification({ primaryText: "Intelligence lookup failed", variant: "error" });
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <>
      <UnlistedIntelligenceModal
        open={modalOpen}
        setOpen={setModalOpen}
        results={results}
        loading={lookupLoading}
      />

      <div className="flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 bg-gray-50/80 px-3 py-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Live Listing
          </span>
        </div>

        <div className="flex flex-1 flex-col gap-2 px-3 py-2">
          <span className="text-xs text-gray-700 line-clamp-3" title={offer?.title ?? undefined}>
            {offer?.title ?? "—"}
          </span>
          <span className="text-sm font-semibold text-gray-900">{PriceFormat(price)}</span>

          {missingKType && (
            <div className="flex flex-col gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-900">
              <div className="flex items-start gap-1.5">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="text-xs font-semibold">No KTYPE</span>
              </div>
              {part.vehicle && <UnlistedKTypeUpdate part={part} />}
            </div>
          )}

          {!missingKType && part.vehicle && (
            <UnlistedKTypeUpdate part={part} />
          )}

          <div className="rounded-md border border-amber-200 bg-amber-50/40 p-2">
            {summary == null ? (
              <button
                type="button"
                onClick={() => runLookup(false)}
                disabled={lookupLoading}
                className="flex w-full items-center justify-center gap-1.5 rounded-md bg-amber-100 px-2 py-1.5 text-xs font-semibold text-amber-900 transition hover:bg-amber-200 disabled:opacity-60"
              >
                <SparklesIcon className="h-3.5 w-3.5" />
                {lookupLoading ? "Loading…" : "Get Intelligence"}
              </button>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[11px] text-amber-900">
                  <span>Min {PriceFormat(summary.min)}</span>
                  <span className="font-semibold">Avg {PriceFormat(summary.avg)}</span>
                  <span>Max {PriceFormat(summary.max)}</span>
                </div>
                {activeCount != null && (
                  <div className="text-[10px] text-amber-800/80">
                    {activeCount} active listing{activeCount === 1 ? "" : "s"}
                  </div>
                )}
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => onStageAvgPrice(part.id, summary.avg)}
                    disabled={stagingPrice}
                    className="flex-1 rounded-md bg-amber-200 px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-300 disabled:opacity-60"
                  >
                    Stage avg price
                  </button>
                  <button
                    type="button"
                    onClick={() => (results ? setModalOpen(true) : runLookup(true))}
                    disabled={lookupLoading}
                    className="rounded-md border border-amber-300 px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                  >
                    Details
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => runLookup(true)}
              disabled={lookupLoading}
              className="mt-1.5 w-full rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-60"
            >
              {lookupLoading ? "Looking up…" : "Refresh intelligence"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
